import React, { useEffect, useState } from "react";
import { FaPlus, FaEdit, FaTrash, FaSpinner } from "react-icons/fa";

interface ClassTest {
  id: number;
  title: string;
  level: string;
  chapterRange: string;
  chapterRangeDisplay: string;
  lesenDuration: number;
  horenDuration: number;
  schreibenDuration: number;
  totalDurationMinutes: number;
  questionCount: number;
  totalPoints: number;
  isActive: boolean;
}

const LEVEL_COLORS: Record<string, string> = {
  A1: "bg-green-100 text-green-800",
  A2: "bg-blue-100 text-blue-800",
  B1: "bg-purple-100 text-purple-800",
  B2: "bg-orange-100 text-orange-800",
};

interface Props {
  onEdit: (id: number) => void;
  onNew: () => void;
}

const AdminClassTestList: React.FC<Props> = ({ onEdit, onNew }) => {
  const [tests, setTests] = useState<ClassTest[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const fetchTests = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/class-tests`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to load");
      setTests(await res.json());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTests();
  }, []);

  const handleDelete = async (id: number) => {
    if (!window.confirm("Delete this class test? This cannot be undone.")) return;
    setDeletingId(id);
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/class-tests/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      setTests((prev) => prev.filter((t) => t.id !== id));
    } finally {
      setDeletingId(null);
    }
  };

  const grouped = ["A1", "A2", "B1", "B2"].reduce<Record<string, ClassTest[]>>((acc, lvl) => {
    acc[lvl] = tests.filter((t) => t.level === lvl);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-800">Class Tests</h2>
        <button
          onClick={onNew}
          className="flex items-center gap-2 px-4 py-2 bg-[#009fe3] text-white rounded-lg hover:bg-[#007cb5] transition-colors"
        >
          <FaPlus /> New Class Test
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <FaSpinner className="animate-spin text-4xl text-[#009fe3]" />
        </div>
      ) : (
        Object.entries(grouped).map(([level, levelTests]) => (
          <div key={level} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-200 bg-gray-50 flex items-center gap-2">
              <span className={`px-2 py-0.5 rounded-full text-sm font-bold ${LEVEL_COLORS[level]}`}>
                {level}
              </span>
              <span className="text-gray-600 text-sm">{levelTests.length} test(s)</span>
            </div>

            {levelTests.length === 0 ? (
              <div className="px-5 py-6 text-gray-400 text-sm">No class tests for {level} yet.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="px-4 py-2 text-left font-semibold text-gray-600">Title</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-600">Chapters</th>
                    <th className="px-4 py-2 text-center font-semibold text-gray-600">Questions</th>
                    <th className="px-4 py-2 text-center font-semibold text-gray-600">Duration</th>
                    <th className="px-4 py-2 text-center font-semibold text-gray-600">Points</th>
                    <th className="px-4 py-2 text-center font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {levelTests.map((t) => (
                    <tr key={t.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">{t.title}</td>
                      <td className="px-4 py-3 text-gray-600">{t.chapterRangeDisplay}</td>
                      <td className="px-4 py-3 text-center text-gray-700">{t.questionCount}</td>
                      <td className="px-4 py-3 text-center text-gray-700">
                        {t.totalDurationMinutes ? `${t.totalDurationMinutes} min` : "—"}
                      </td>
                      <td className="px-4 py-3 text-center text-gray-700">{t.totalPoints}</td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => onEdit(t.id)}
                            className="p-1.5 text-[#009fe3] hover:bg-blue-50 rounded transition-colors"
                            title="Edit & manage questions"
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => handleDelete(t.id)}
                            disabled={deletingId === t.id}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                            title="Delete"
                          >
                            {deletingId === t.id ? <FaSpinner className="animate-spin" /> : <FaTrash />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))
      )}
    </div>
  );
};

export default AdminClassTestList;
